import { memo, useCallback } from "react";
import type { ReactElement, ReactNode } from "react";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import { styled } from "@mui/material";
import { DataTableColumn } from "../VirtualizedDataTable";

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  // This CSS variable is used to allow the background color to be overridden when the "new-item" class is applied
  "--bg-color": theme.palette.common.white,
  backgroundColor: "var(--bg-color)",

  "&.MuiTableRow-hover": {
    cursor: "pointer",

    "&:hover": {
      backgroundColor: theme.palette.grey[20],
    },
  },

  "&:last-child td, &:last-child th": {
    border: 0,
  },
}));

const StyledTableCellBody = memo(
  styled(TableCell)(({ theme }) => ({
    "&.MuiTableCell-root": {
      color: "inherit",
      borderColor: theme.palette.grey[40],
    },

    ...theme.typography.TextS,
    fontFamily: theme.typography.fontFamily,

    "&.MuiTableCell-sizeSmall": {
      padding: "8px 2px",
    },

    "&.MuiTableCell-sizeMedium": {
      padding: "12px 16px",
    },
  }))
);

type BodyRowSlotProps = Omit<React.ComponentProps<typeof TableRow>, "ref" | "onClick" | "className">;
type BodyCellSlotProps = Omit<React.ComponentProps<typeof TableCell>, "onClick" | "className">;

interface DataTableRowProps<T> {
  row: T;
  rowIndex: number;
  /** 0-based position of this row among all rows in the table, including the header */
  tableRowIndex: number;
  columns: readonly DataTableColumn<T>[];
  onRowClick?: (row: T, rowIndex: number) => void;
  className?: (row: T) => string | undefined;
  slotProps?: {
    bodyRow?: BodyRowSlotProps;
    bodyCell?: BodyCellSlotProps;
  };
}

function DataTableRowImpl<T>(props: DataTableRowProps<T>) {
  const { row, rowIndex, tableRowIndex, columns, slotProps, onRowClick, className } = props;

  const handleRowClick = useCallback(() => {
    onRowClick?.(row, rowIndex);
  }, [onRowClick, row, rowIndex]);

  return (
    <StyledTableRow
      tabIndex={-1}
      {...slotProps?.bodyRow}
      data-index={rowIndex}
      aria-rowindex={tableRowIndex + 1}
      onClick={handleRowClick}
      className={className?.(row)}
    >
      {columns.map((column) => {
        // column.id can be null for non-data columns, in which case we pass undefined to the cell renderer
        const value = column.id ? row[column.id as keyof T] : undefined;
        const content = column.cell
          ? (column.cell as (v: unknown, r: T, i: number) => ReactNode)(value, row, rowIndex)
          : (value as ReactNode);

        const handleCellClick = (event: React.MouseEvent<HTMLTableCellElement>) => {
          column.onCellClick?.(event, value as T[keyof T], row as T, rowIndex as number);
        };

        return (
          <StyledTableCellBody
            key={String(column.id)}
            align={column.align}
            style={!!column.width ? { width: column.width } : undefined}
            {...slotProps?.bodyCell}
            onClick={handleCellClick}
            className={column.className?.(row as T)}
            data-column-id={column.id ?? undefined}
          >
            {content}
          </StyledTableCellBody>
        );
      })}
    </StyledTableRow>
  );
}

const DataTableRow = memo(DataTableRowImpl) as <T>(props: DataTableRowProps<T>) => ReactElement;

export default DataTableRow;
